import { svgSparkles } from '@shared/assets/icons';
import {
  CircularProgress,
  Icon,
  Separator,
  Text,
  Tooltip,
} from '@shared/components';
import usePlace from '@shared/graphql/hooks/places/usePlace';
import { Place } from '@shared/types/place';
import { makeElementClassNameFactory, makeRootClassName } from '@shared/utils';
import {
  formatAddressLineOne,
  formatAddressLineTwo,
} from 'clerk_common/stringification/address';
import clsx from 'clsx';
import { LocationEntityCardOptions } from './LocationEntityCardOptions';

export const ROOT = makeRootClassName('LocationEntity');
export const el = makeElementClassNameFactory(ROOT);

type LocationEntityCardContentsProps = {
  place: Place;
  setEntityId: (id: string) => void;
  openLocation: (id: string) => void;
};

function hasNotes(value: string | undefined): boolean {
  return Boolean(value?.trim());
}

function LocationEntityCardContents({
  place,
  setEntityId,
  openLocation,
}: LocationEntityCardContentsProps) {
  const hasInternalNotes = hasNotes(place.notes?.internalNotes);
  const hasExternalNotes = hasNotes(place.notes?.externalNotes);
  const hasAnyNotes = hasInternalNotes || hasExternalNotes;

  return (
    <div className={el`content`}>
      <Icon
        content={svgSparkles}
        size="xs"
        className={el`sparkles-icon`}
        tooltipContent="We matched a known facility for this address"
      />
      <div className={el`place-details`}>
        <div className={el`header`}>
          <Text type="body-xs" isHeavy>
            {place.name}
          </Text>
          {place.deletedAt ? (
            <Tooltip content="Please select a location that has not been deleted">
              <div>
                <Text
                  type="body-xs"
                  isHeavy
                  className="rounded-md bg-red-100 px-2 py-1 text-red-700"
                >
                  Deleted
                </Text>
              </div>
            </Tooltip>
          ) : (
            <LocationEntityCardOptions
              onEditAddress={() => setEntityId('')}
              onEditLocationFile={() => openLocation(place.id)}
            />
          )}
        </div>
        <Text type="body-xs">{formatAddressLineOne(place.address)}</Text>
        <Text type="body-xs">{formatAddressLineTwo(place.address)}</Text>
        {hasAnyNotes && (
          <div className={el`notes-indicator`}>
            <Separator className="my-[6px]" />
            {hasInternalNotes && (
              <Tooltip
                content={place.notes!.internalNotes.trim()}
                title="Internal Notes"
                titleClassName="text-left"
                contentClassName="text-left max-w-[200px]"
              >
                <div className={el`notes-container`}>
                  <Text type="body-xs">Internal Notes</Text>
                  <Text type="body-xs" className={el`notes-preview`}>
                    {place.notes!.internalNotes}
                  </Text>
                </div>
              </Tooltip>
            )}
            {hasExternalNotes && (
              <Tooltip
                content={place.notes!.externalNotes.trim()}
                title="External Notes"
                titleClassName="text-left"
                contentClassName="text-left max-w-[200px]"
              >
                <div className={el`notes-row`}>
                  <Text type="body-xs">External Notes</Text>
                  <Text type="body-xs" className={el`notes-preview`}>
                    {place.notes!.externalNotes}
                  </Text>
                </div>
              </Tooltip>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

type LocationEntityCardProps = {
  id: string;
  setEntityId: (id: string) => void;
  openLocation: (id: string) => void;
};
export function LocationEntityCard({
  id,
  setEntityId,
  openLocation,
}: LocationEntityCardProps) {
  const { place, isLoading } = usePlace({ id: id, includeDeleted: true });

  if (isLoading) {
    return (
      <div className={ROOT}>
        <div className={el`card`}>
          <CircularProgress size="xs" />
        </div>
      </div>
    );
  }
  if (!place) {
    return (
      <div className={ROOT}>
        <Text type="body-xs">{`Place with id ${id} not found`}</Text>
      </div>
    );
  }
  return (
    <div className={ROOT}>
      <div className={clsx(el`card`, place.deletedAt ? 'opacity-50' : '')}>
        <LocationEntityCardContents
          place={place}
          setEntityId={setEntityId}
          openLocation={openLocation}
        />
      </div>
    </div>
  );
}
